import { z } from "zod/v4";
import { createEndpointBase } from "./core";
import { DataSourceMetadataSchema } from "../ea.schema";
import { CountResponseSchema } from "../ea.schema.endpoints";
import { GetRequestBaseResponseSchema, DataSourcesFilterSchema } from "../ea.schema.query";

const base = "data-sources";
const ds = createEndpointBase(base);
const dsLocal = createEndpointBase(base, "local");
const dsGlobal = createEndpointBase(base, "global");

export const dataSourcesEndpoints = {
  dataSourcesGet: ds({
    method: "GET",
    path: "",
    request: z.object({ filter: DataSourcesFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(DataSourceMetadataSchema),
    description: "Retrieves a list of data sources with filtering support.",
  }),
  dataSourcesCount: ds({
    method: "GET",
    path: "count",
    request: z
      .object({ filter: DataSourcesFilterSchema.pick({ where: true }).optional() })
      .optional(),
    response: CountResponseSchema,
    description: "Returns the total number of data sources matching the filters.",
  }),
  dataSourcesDelete: ds({
    method: "DELETE",
    path: ":id",
    request: z.object({ id: z.number() }),
    response: z.any(),
    description: "Permanently deletes the specified data source.",
  }),
  dataSourcesLocalUpload: dsLocal({
    method: "POST",
    path: "upload/:scriptId",
    request: z.any(),
    response: z.any(),
    description: "Uploads a data source file for a specific script.",
  }),
  dataSourcesGlobalUpdate: dsGlobal({
    method: "POST",
    path: "update/:datasourceId",
    request: z
      .object({
        projectIds: z
          .union([
            z.array(z.number()),
            z.preprocess(
              (v: string) => (v ? v.split(",").map((v) => parseInt(v)) : []),
              z.array(z.number()),
            ),
          ])
          .optional(),
      })
      .optional(),
    response: z.any(),
    description:
      "Updates a global data source, optionally replacing the file and project assignments.",
  }),
  dataSourcesGlobalUpload: dsGlobal({
    method: "POST",
    path: "upload",
    request: z.object({
      projectIds: z.preprocess(
        (v: string) => (v ? v.split(",").map((v) => parseInt(v)) : []),
        z.array(z.number()),
      ),
    }),
    response: z.any(),
    description: "Uploads a global data source file and assigns it to projects.",
  }),
  dataSourcesGlobalProjectExcels: dsGlobal({
    method: "GET",
    path: "project-excels",
    request: z.object({ projectId: z.coerce.number() }),
    response: z.any(),
    description: "Retrieves Excel data sources for a specific project.",
  }),
  dataSourcesGlobalAssignToScript: dsGlobal({
    method: "POST",
    path: ":datasourceId/script/:scriptId",
    request: z.any(),
    response: z.any(),
    description: "Assigns a data source to a script.",
  }),
  dataSourcesGlobalUnassignFromScript: dsGlobal({
    method: "POST",
    path: "unassign/:scriptId",
    request: z.any(),
    response: z.any(),
    description: "Unassigns a data source from a script.",
  }),
  dataSourcesDownload: ds({
    method: "POST",
    path: "download",
    request: z.object({ id: z.coerce.number() }),
    response: z.any(),
    description: "Downloads a data source file.",
  }),
  dataSourcesGenerate: ds({
    method: "POST",
    path: "generate",
    request: z.object({ scriptId: z.coerce.number() }),
    response: z.any(),
    description: "Generates a data source template for a script.",
  }),
} as const;
